import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, ChevronDown, Sparkles, Loader2, User, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

const QUICK_REPLIES = [
  { label: 'Help me book a trip', message: 'I need help booking a luxury trip to Albania' },
  { label: 'Albanian Riviera', message: 'Tell me about the Albanian Riviera and the best beaches to visit' },
  { label: 'Villa availability', message: 'I\'d like to know about villa availability and your best properties' },
  { label: 'Yacht charters', message: 'What yacht charter options do you offer along the Albanian coast?' },
  { label: 'Best time to visit', message: 'When is the best time to visit Albania?' },
  { label: 'Bespoke itinerary', message: 'I\'d like help creating a bespoke travel itinerary for Albania' },
];

const WELCOME_MESSAGE: ChatMessage = {
  id: 'welcome',
  role: 'assistant',
  content: 'Welcome to Zera Travel. I\'m your personal concierge, here to help you discover the extraordinary beauty of Albania. How may I assist you today?',
  created_at: new Date().toISOString(),
};

function getSessionId(): string {
  const key = 'zera_chat_session_id';
  let sessionId = sessionStorage.getItem(key);
  if (!sessionId) {
    sessionId = 'sess_' + crypto.randomUUID();
    sessionStorage.setItem(key, sessionId);
  }
  return sessionId;
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const TypingIndicator: React.FC = () => (
  <div className="flex items-end gap-2 mb-4">
    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#C9A961] to-[#A88B45] flex items-center justify-center flex-shrink-0">
      <Sparkles className="w-3.5 h-3.5 text-white" />
    </div>
    <div className="bg-white border border-[#E8DFD0] rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
      <div className="flex gap-1.5 items-center h-4">
        <span className="w-2 h-2 bg-[#C9A961] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-2 h-2 bg-[#C9A961] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-2 h-2 bg-[#C9A961] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  </div>
);

const ChatWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MESSAGE]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const chatBodyRef = useRef<HTMLDivElement>(null);

  const { user, setShowAuthModal } = useAuth();

  const sessionId = useRef(getSessionId());

  // Scroll to bottom of messages
  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
      setHasNewMessage(false);
    }
  }, [isOpen]);

  // Load chat history when panel opens or user logs in
  useEffect(() => {
    if (!isOpen || historyLoaded) return;

    const loadHistory = async () => {
      setIsLoadingHistory(true);
      try {
        let query = supabase
          .from('chat_messages')
          .select('*')
          .order('created_at', { ascending: true })
          .limit(50);

        if (user) {
          query = query.eq('user_id', user.id);
        } else {
          query = query.eq('session_id', sessionId.current).is('user_id', null);
        }

        const { data, error } = await query;

        if (!error && data && data.length > 0) {
          setMessages([WELCOME_MESSAGE, ...data]);
          setShowQuickReplies(false);
        }
      } catch (err) {
        console.error('Failed to load chat history:', err);
      }
      setIsLoadingHistory(false);
      setHistoryLoaded(true);
    };

    loadHistory();
  }, [isOpen, user, historyLoaded]);

  // Reset history loaded state when user changes
  useEffect(() => {
    setHistoryLoaded(false);
  }, [user?.id]);

  // Save message to database
  const saveMessage = useCallback(async (role: 'user' | 'assistant', content: string) => {
    try {
      const insertData: Record<string, unknown> = {
        role,
        content,
      };

      if (user) {
        insertData.user_id = user.id;
      } else {
        insertData.session_id = sessionId.current;
      }

      await supabase.from('chat_messages').insert(insertData);
    } catch (err) {
      console.error('Failed to save message:', err);
    }
  }, [user]);

  // Send message to concierge
  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: content.trim(),
      created_at: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setShowQuickReplies(false);
    setIsTyping(true);

    // Save user message to DB
    saveMessage('user', content.trim());

    try {
      // Build chat history for context (exclude welcome message)
      const chatHistory = messages
        .filter(m => m.id !== 'welcome')
        .slice(-10)
        .map(m => ({ role: m.role, content: m.content }));

      const { data, error } = await supabase.functions.invoke('concierge-chat', {
        body: {
          message: content.trim(),
          chatHistory,
          userId: user?.id || null,
          sessionId: sessionId.current,
        },
      });

      const reply = data?.reply || 'Thank you for your message. Our concierge team will be in touch shortly.';

      const assistantMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: reply,
        created_at: new Date().toISOString(),
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Save assistant message to DB
      saveMessage('assistant', reply);

      // Show notification dot if chat is closed
      if (!isOpen) {
        setHasNewMessage(true);
      }
    } catch (err) {
      console.error('Chat error:', err);
      const errorMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: 'I apologize for the interruption. Please try again, or contact our team directly for immediate assistance.',
        created_at: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    }

    setIsTyping(false);
  }, [messages, user, isOpen, saveMessage]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleQuickReply = (message: string) => {
    sendMessage(message);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  return (
    <>
      {/* Chat Panel */}
      <div
        className={`fixed bottom-24 right-4 sm:right-6 z-[9999] transition-all duration-500 ease-out ${
          isOpen
            ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto'
            : 'opacity-0 translate-y-4 scale-95 pointer-events-none'
        }`}
      >
        <div className="w-[360px] sm:w-[400px] h-[560px] bg-[#FAF8F3] rounded-2xl shadow-2xl border border-[#E8DFD0] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#1A3B5C] to-[#0D2440] px-5 py-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9A961] to-[#A88B45] flex items-center justify-center shadow-lg">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-serif text-white text-sm tracking-wide">Zera Concierge</h3>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-[10px] text-white/60 tracking-wider uppercase font-sans">Online</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
                aria-label="Minimize chat"
              >
                <ChevronDown className="w-5 h-5 text-white/70" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
                aria-label="Close chat"
              >
                <X className="w-4 h-4 text-white/70" />
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div
            ref={chatBodyRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-1 scroll-smooth"
            style={{ scrollbarWidth: 'thin', scrollbarColor: '#C9A961 transparent' }}
          >
            {isLoadingHistory && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 text-[#C9A961] animate-spin" />
                <span className="text-xs text-[#999] ml-2 font-sans">Loading conversation...</span>
              </div>
            )}

            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'items-end gap-2'} mb-3`}>
                {msg.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#C9A961] to-[#A88B45] flex items-center justify-center flex-shrink-0 shadow-sm">
                    <Sparkles className="w-3.5 h-3.5 text-white" />
                  </div>
                )}
                <div className={`max-w-[80%] ${msg.role === 'user' ? 'order-1' : ''}`}>
                  <div
                    className={`px-4 py-3 text-[13px] leading-relaxed font-sans ${
                      msg.role === 'user'
                        ? 'bg-gradient-to-br from-[#1A3B5C] to-[#0D2440] text-white rounded-2xl rounded-br-sm shadow-md'
                        : 'bg-white border border-[#E8DFD0] text-[#333] rounded-2xl rounded-bl-sm shadow-sm'
                    }`}
                  >
                    {msg.content}
                  </div>
                  <span className={`text-[9px] text-[#BBB] mt-1 block font-sans ${msg.role === 'user' ? 'text-right' : 'text-left'}`}>
                    {formatTime(msg.created_at)}
                  </span>
                </div>
                {msg.role === 'user' && (
                  <div className="w-7 h-7 rounded-full bg-[#1A3B5C] flex items-center justify-center flex-shrink-0 shadow-sm order-2">
                    <User className="w-3.5 h-3.5 text-white" />
                  </div>
                )}
              </div>
            ))}

            {isTyping && <TypingIndicator />}

            {/* Quick Replies */}
            {showQuickReplies && !isTyping && messages.length <= 1 && (
              <div className="pt-2">
                <p className="text-[10px] text-[#999] uppercase tracking-widest font-sans mb-3">Quick questions</p>
                <div className="flex flex-wrap gap-2">
                  {QUICK_REPLIES.map((qr) => (
                    <button
                      key={qr.label}
                      onClick={() => handleQuickReply(qr.message)}
                      className="group flex items-center gap-1.5 px-3 py-2 bg-white border border-[#E8DFD0] rounded-full text-[11px] font-sans text-[#555] hover:border-[#C9A961] hover:text-[#C9A961] hover:bg-[#FDFBF5] transition-all duration-200 shadow-sm"
                    >
                      {qr.label}
                      <ArrowRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* User info banner for guests */}
          {!user && messages.length > 3 && (
            <div className="px-4 py-2 bg-[#F5F1E8] border-t border-[#E8DFD0] flex-shrink-0">
              <p className="text-[10px] text-[#888] font-sans text-center">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    setTimeout(() => setShowAuthModal(true), 300);
                  }}
                  className="text-[#C9A961] hover:underline font-medium"
                >
                  Sign in
                </button>
                {' '}to save your conversation history across sessions
              </p>
            </div>
          )}


          {/* Input Area */}
          <div className="border-t border-[#E8DFD0] bg-white px-4 py-3 flex-shrink-0">
            <form onSubmit={handleSubmit} className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask your concierge anything..."
                className="flex-1 bg-[#F5F3EE] border border-[#E8DFD0] rounded-full px-4 py-2.5 text-[13px] font-sans text-[#333] placeholder:text-[#BBB] focus:outline-none focus:border-[#C9A961] focus:ring-1 focus:ring-[#C9A961]/30 transition-all"
                disabled={isTyping}
              />
              <button
                type="submit"
                disabled={!inputValue.trim() || isTyping}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 flex-shrink-0 ${
                  inputValue.trim() && !isTyping
                    ? 'bg-gradient-to-br from-[#C9A961] to-[#A88B45] text-white shadow-lg hover:shadow-xl hover:scale-105 active:scale-95'
                    : 'bg-[#E8DFD0] text-[#BBB] cursor-not-allowed'
                }`}
                aria-label="Send message"
              >
                {isTyping ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </form>
            <p className="text-[9px] text-[#CCC] text-center mt-2 font-sans">
              Powered by Zera AI Concierge
            </p>
          </div>
        </div>
      </div>

      {/* Floating Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-4 sm:right-6 z-[9999] group transition-all duration-500 ${
          isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'
        }`}
        aria-label="Open concierge chat"
      >
        {/* Pulse ring */}
        <span className="absolute inset-0 rounded-full bg-[#C9A961] animate-ping opacity-20" />

        {/* Button */}
        <div className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-[#C9A961] to-[#A88B45] shadow-xl hover:shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95">
          <MessageCircle className="w-6 h-6 sm:w-7 sm:h-7 text-white" />

          {/* New message indicator */}
          {hasNewMessage && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center shadow-md">
              <span className="w-2 h-2 bg-white rounded-full" />
            </span>
          )}
        </div>

        {/* Tooltip */}
        <div className="absolute bottom-full right-0 mb-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          <div className="bg-[#1A3B5C] text-white text-[11px] font-sans tracking-wide px-4 py-2 rounded-lg shadow-lg whitespace-nowrap">
            Chat with our Concierge
            <div className="absolute -bottom-1 right-6 w-2 h-2 bg-[#1A3B5C] rotate-45" />
          </div>
        </div>
      </button>
    </>
  );
};

export default ChatWidget;
